import React from 'react'
import Table from '../../table'
import {
  PayrollWeeksTableProps,
  buildWeekRowData,
  useWeeksTableColumns,
  calculateTotals,
  filterEventsByOccurrence,
  createTotalRow,
} from './index'
import { uniqBy } from 'lodash'

/**
 * Composant pour afficher les données de paie par semaine pour un worker
 *
 * Affiche un tableau avec une ligne par semaine contenant :
 * - Numéro de semaine et année
 * - Période de la semaine
 * - Heures contractuelles vs travaillées
 * - Heures supplémentaires
 * - Pauses
 * - Statut de validation et enregistrement
 */
const PayrollWeeksTable: React.FC<PayrollWeeksTableProps> = ({
  worker,
  weeks,
  events,
  onCloseWeek,
}) => {
  // Récupérer les employer rules depuis la première semaine disponible
  const employerRules = uniqBy(
    worker.weeksSummary?.weeks.flatMap(w => w.employerRules) || [],
    'ruleId',
  )
  // Calculer les totaux
  const {
    totalBenefitEvents,
    totalInformativeEvents,
    totalInformativeEventsByEvent,
    totalEmployerRulesValues,
    totalEmployerRulesDecimalValues,
    negativeContractDiff,
  } = calculateTotals(weeks, worker, events, employerRules)

  // Filtrer les events par occurrence
  const { visibleBenefitEvents } = filterEventsByOccurrence(events, totalBenefitEvents)

  const hasInformativeEvents = totalInformativeEvents > 0

  const tableColumns = useWeeksTableColumns(
    worker.weeklyHoursWorked,
    employerRules,
    visibleBenefitEvents,
    hasInformativeEvents,
    onCloseWeek,
  )

  // Transformation des données en lignes de tableau
  const data = React.useMemo(
    () => weeks.map(week => buildWeekRowData(week, worker, events, employerRules)),
    [weeks, worker, events, employerRules],
  )

  // Ajouter la ligne de totaux
  data.push(
    createTotalRow(
      data,
      worker,
      totalEmployerRulesValues,
      totalEmployerRulesDecimalValues,
      totalBenefitEvents,
      totalInformativeEvents,
      totalInformativeEventsByEvent,
      events,
      negativeContractDiff,
    ),
  )

  return (
    <>
      <Table
        columns={tableColumns}
        data={data}
        noDataMessage="Aucune semaine à afficher"
        dataTest="payroll-weeks-table"
      />
    </>
  )
}

export default PayrollWeeksTable
